import React, { useState, useEffect, useCallback, useRef } from 'react';
import './GameBoard.css';
import GameLogic from './GameLogic';
import GameHistory from './GameHistory';
import GameInfo from './GameInfo';
import MobileGameHeader from './MobileGameHeader';
import KebabMenu from './KebabMenu';
import MoveHistoryOverlay from './MoveHistoryOverlay';
import { exportBoardPosition, exportGameHistoryCsv } from '../utils/gameExports';
import { api } from '../utils/api';

// Disable all console.log for performance (CPU optimization)
const ENABLE_LOGGING = false;
const log = ENABLE_LOGGING ? console.log.bind(console) : () => {};

// Piece symbols for text display
const PIECE_SYMBOLS = {
  white: {
    king: '♔', knight: '♘', pawn: '♙', advisor: '♕', cannon: '♖', camel: '♗', donkey: '♞', ram: '♜', spy: '♟'
  },
  black: {
    king: '♚', knight: '♞', pawn: '♟', advisor: '♛', cannon: '♜', camel: '♝', donkey: '♘', ram: '♖', spy: '♙'
  }
};

// Import piece images
const PIECE_IMAGES = {
  white: {
    king: '/assets/pieces/white-king.png',
    knight: '/assets/pieces/white-knight.png',
    pawn: '/assets/pieces/white-pawn.png',
    advisor: '/assets/pieces/white-advisor.png',
    cannon: '/assets/pieces/white-cannon.png',
    camel: '/assets/pieces/white-camel.png',
    donkey: '/assets/pieces/white-donkey.png',
    ram: '/assets/pieces/white-ram.png',
    spy: '/assets/pieces/white-spy.png'
  },
  black: {
    king: '/assets/pieces/black-king.png',
    knight: '/assets/pieces/black-knight.png',
    pawn: '/assets/pieces/black-pawn.png',
    advisor: '/assets/pieces/black-advisor.png',
    cannon: '/assets/pieces/black-cannon.png',
    camel: '/assets/pieces/black-camel.png',
    donkey: '/assets/pieces/black-donkey.png',
    ram: '/assets/pieces/black-ram.png',
    spy: '/assets/pieces/black-spy.png'
  }
};

const GameBoard = ({ mode, gameData, onBackToMenu, onBackToSetup, onSaveAndExit,
                     onMove, opponentMove, onGameOver }) => {
  // Only log on first render or when gameData changes
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  
  if (renderCountRef.current <= 3 || renderCountRef.current % 10 === 0) {
    // Component render tracking
  }
  
  const [board, setBoard] = useState(null);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState('white');
  const [gameState, setGameState] = useState(null);
  const [gameInitialized, setGameInitialized] = useState(false);
  const [promotionDialog, setPromotionDialog] = useState(null);
  const [gameLogic] = useState(new GameLogic());
  const [availableImages, setAvailableImages] = useState({});
  const [aiThinking, setAiThinking] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ white: 0, black: 0 }); // Total accumulated time
  const [turnTimeLeft, setTurnTimeLeft] = useState({ white: 0, black: 0 }); // Display only - current turn elapsed time
  const [boardRenderKey, setBoardRenderKey] = useState(0);
  const [forceRender, setForceRender] = useState(0);
  const [invalidSquare, setInvalidSquare] = useState(null);
  const [frozenKingZones, setFrozenKingZones] = useState({});
  const [lastAiMove, setLastAiMove] = useState(null); // **ADDED BACK**: For move highlighting
  const [lastHumanMove, setLastHumanMove] = useState(null); // Track last human move for pondering
  const [serverCheckStatus, setServerCheckStatus] = useState({ white: false, black: false }); // Server's check status
  const [hasServerCheckStatus, setHasServerCheckStatus] = useState(false); // Whether we've received server check status
  const [showMobileActions, setShowMobileActions] = useState(false); // Mobile game actions collapse state (legacy — kebab replaced this)
  const [showMoveHistory, setShowMoveHistory] = useState(false); // Mobile move-history overlay
  const [aiError, setAiError] = useState(null); // { message, kind } when AI move request fails — surfaces retry banner
  const timeIntervalRef = useRef(null);
  const turnTimeIntervalRef = useRef(null);
  const turnStartTimeRef = useRef({ white: null, black: null }); // Timestamp when turn started

  // Use a variable to hold the next player to avoid race conditions
  const [nextMovePlayer, setNextMovePlayer] = useState(null);
  
  // **FIX RACE CONDITION**: Add AI move lock to prevent multiple simultaneous AI moves
  const aiMoveLockRef = useRef(false);

  // Forward declare makeAiMove using useRef to break circular dependency
  const makeAiMoveRef = React.useRef();

  // ===== DIAGNOSTIC: visual-disappearing-pieces bug =====
  // User reports pieces (both colors, game 35 + 36) visually vanish during AI
  // think and return after the AI move. Could not reproduce locally and could
  // not find a CSS/render path that would hide pieces. Adding piece-count +
  // image-availability + lifecycle snapshots so the next reproduction surfaces
  // real data. Visible in DevTools console — search for "[piece-debug]".
  //
  // Specifically watching for:
  //   - board going null or losing pieces (server/state bug)
  //   - availableImages dropping back to {} (remount → re-running checkImages)
  //   - render-counter spikes (excessive re-renders during the wait)
  // Reuses the existing renderCountRef declared above for "renders" field.
  useEffect(() => {
    console.log('[piece-debug] MOUNT', new Date().toISOString(),
                { renders: renderCountRef.current });
    return () => {
      console.log('[piece-debug] UNMOUNT', new Date().toISOString(),
                  { renders: renderCountRef.current });
    };
  }, []);
  useEffect(() => {
    if (!board) {
      console.log('[piece-debug] board=null', new Date().toISOString(),
                  { aiThinking, currentPlayer, renders: renderCountRef.current });
      return;
    }
    let w = 0, b = 0, empty = 0;
    for (let r = 0; r < 9; r++) for (let c = 0; c < 9; c++) {
      const p = board[r]?.[c];
      if (!p) empty++;
      else if (p.color === 'white') w++;
      else if (p.color === 'black') b++;
    }
    const imgKeys = Object.keys(availableImages || {});
    const imgsTrue = imgKeys.filter(k => availableImages[k]).length;
    console.log('[piece-debug]', new Date().toISOString(),
                { white: w, black: b, empty, total: w+b+empty,
                  aiThinking, currentPlayer,
                  imgsAvail: `${imgsTrue}/${imgKeys.length}`,
                  boardRenderKey, forceRender,
                  renders: renderCountRef.current });
  }, [board, aiThinking, currentPlayer, availableImages, boardRenderKey, forceRender]);

  // Effect to detect available images
  useEffect(() => {
    const checkImages = async () => {
      const images = {};
      for (const color of ['white', 'black']) {
        for (const piece of ['king', 'knight', 'pawn', 'advisor', 'cannon', 'camel', 'donkey', 'ram', 'spy']) {
          try {
            const img = new Image();
            img.src = PIECE_IMAGES[color][piece];
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
            });
            images[`${color}-${piece}`] = true;
          } catch (error) {
            images[`${color}-${piece}`] = false;
          }
        }
      }
      setAvailableImages(images);
    };
    checkImages();
  }, []);

  // Time control effects - Times are initialized in useState above

  // When currentPlayer changes, record the turn start time
  useEffect(() => {
    if (gameState && !gameState.gameOver && gameInitialized) {
      turnStartTimeRef.current[currentPlayer] = Date.now();
      log(`⏱️ Turn started for ${currentPlayer}`);
    }
  }, [currentPlayer, gameState, gameInitialized]);

  // Display timer - updates every second for UI purposes only
  useEffect(() => {
    if (gameState && !gameState.gameOver && gameInitialized && turnStartTimeRef.current[currentPlayer]) {
      turnTimeIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - turnStartTimeRef.current[currentPlayer]) / 1000);
        setTurnTimeLeft(prev => {
          if (prev[currentPlayer] !== elapsed) {
            return { ...prev, [currentPlayer]: elapsed };
          }
          return prev;
        });
      }, 1000); // Update every 1 second (CPU optimization: reduced from 100ms)
    } else {
      clearInterval(turnTimeIntervalRef.current);
    }

    return () => clearInterval(turnTimeIntervalRef.current);
  }, [currentPlayer, gameState, gameInitialized]);

  const isAIPlayer = useCallback((player) => {
    console.log('🔍 isAIPlayer called:', { 
      player, 
      mode, 
      gameData: gameData ? {
        whitePlayerName: gameData.whitePlayerName,
        blackPlayerName: gameData.blackPlayerName,
        gameMode: gameData.gameMode
      } : null
    });
    
    if (mode !== 'ai' || !gameData) {
      console.log('❌ isAIPlayer: Not AI mode or no gameData');
      return false;
    }
    const playerName = player === 'white' ? gameData.whitePlayerName : gameData.blackPlayerName;
    const result = playerName && playerName.includes('AI');
    console.log('🔍 isAIPlayer result:', { player, playerName, result });
    return result;
  }, [mode, gameData]);

  const makeMove = useCallback(async (fromRow, fromCol, toRow, toCol, isAiMove = false, promotionPiece = null, isRemoteMove = false) => {
    if (!board || !gameState || gameState.gameOver) {
      return;
    }

    const piece = board[fromRow][fromCol];
    if (!piece) {
      return;
    }

    // **CHECK FOR PAWN PROMOTION** - Show dialog for human players, auto-promote for AI
    if (piece.type === 'pawn' && !promotionPiece) {
      const isWhitePawn = piece.color === 'white';
      const promotionRow = isWhitePawn ? 8 : 0; // White pawns promote on row 8, black on row 0
      
      if (toRow === promotionRow) {
        if (isAiMove) {
          // Server-side pickBestPromotion normally supplies promotionPiece via
          // the AI-move response (see makeAiMove). This fallback fires only
          // when the server didn't include one (older server build, path that
          // bypasses the promotion-picker, etc.). Donkey is a safe default —
          // it's the highest-value piece (6 pawns) so any random tactical
          // slot beyond pure material fits are handled by the server picker.
          promotionPiece = 'donkey';
        } else {
          // Human player - show promotion dialog
          setPromotionDialog({
            row: toRow,
            col: toCol,
            color: piece.color,
            fromRow,
            fromCol
          });
          return; // Exit early - promotion dialog will handle the move
        }
      }
    }

    // **DETERMINE CAPTURED PIECE FIRST** - Define this before server validation
    const capturedPiece = board[toRow][toCol];

    // **DECLARE MOVE RESPONSE OUTSIDE TRY BLOCK** - For access in UI update section
    let moveResponse = null;

    // **VALIDATE WITH SERVER** - SERVER HANDLES ALL FROZEN KING LOGIC
    // Skipped for remote moves in online mode: those already went through the
    // opponent's REST validation. The socket broadcast is authoritative for
    // this Phase-1 client-trust model; Phase 2 will move validation to the
    // socket path itself.
    if (gameData?.id && !isRemoteMove) {
      try {
        const moveData = {
          fromRow,
          fromCol,
          toRow,
          toCol,
          pieceType: piece.type,
          capturedPiece: capturedPiece ? capturedPiece.type : null,
          moveNumber: (gameState?.moves?.length || 0) + 1,
          playerColor: piece.color,
          boardState: board, // Server handles all frozen zone logic
          frozenKingZones: frozenKingZones, // Send existing frozen zones to preserve original positions
          promotionPiece: promotionPiece // Include promotion piece if provided
        };
        
        moveResponse = await api.saveMove(gameData.id, moveData);
        
        // **IMMEDIATELY UPDATE FROZEN ZONES FROM SERVER RESPONSE**
        if (moveResponse.frozenKingZones) {
          setFrozenKingZones(moveResponse.frozenKingZones);
        }

        // **UPDATE CHECK STATUS FROM SERVER RESPONSE**
        if (moveResponse.checkStatus) {
          setServerCheckStatus(moveResponse.checkStatus);
          setHasServerCheckStatus(true);
        }

        // **DETERMINE ACTUAL CAPTURE FROM SERVER** - Don't assume moves to occupied squares are captures
        const actualCapturedPiece = capturedPiece; // For most pieces, this is correct
        let actualCapture = null;
        
        // For ram moves, wait for server to tell us if there was actually a capture
        if (piece.type === 'ram' && capturedPiece) {
          // The server will tell us if this was a real capture (bumped off board) or just a bump
          // For now, assume no capture for rams (server will correct this in move history if wrong)
          actualCapture = null;
          log('🐏 CLIENT: Ram move detected - server will determine if capture occurred');
        } else if (capturedPiece) {
          // Non-ram pieces: if there was a piece on destination, it was captured
          actualCapture = capturedPiece.type;
        }
        
        // **HANDLE GAME OVER STATE FROM SERVER**
        if (moveResponse.gameOver !== undefined && moveResponse.gameOver) {
            // const winnerText = moveResponse.winner ? 
            //   `${moveResponse.winner.charAt(0).toUpperCase() + moveResponse.winner.slice(1)} wins!` : 
            //   'Draw';
            const reason = moveResponse.winner ? 
              `Checkmate! ${moveResponse.winner.charAt(0).toUpperCase() + moveResponse.winner.slice(1)} wins!` : 
              'Stalemate! The game is a draw.';
            
            setGameState(prevState => ({
              ...prevState,
              gameOver: true,
              winner: moveResponse.winner,
              gameOverReason: reason
            }));
            
            // Show game over message
            setTimeout(() => {
              alert(reason);
            }, 500);
        }
        
      } catch (error) {
        console.error('❌ SERVER VALIDATION FAILED:', error);
        // Revert the move if server validation fails
        setBoard(prevBoard => [...prevBoard]);
        setSelectedSquare(null);
        return;
      }
    }

    // **ONLY UPDATE UI AFTER SERVER APPROVAL** 
    // Create a completely new board with a new reference to ensure React detects the change
    const newBoard = board.map(row => row.map(cell => cell ? { ...cell } : null));

    // Move the piece
    let movedPiece = { ...newBoard[fromRow][fromCol], hasMoved: true };
    
    // Handle pawn promotion on the client side for UI updates
    if (promotionPiece && piece.type === 'pawn') {
      movedPiece = { type: promotionPiece, color: piece.color, hasMoved: true };
    }
    
    // **FIXED**: Handle server-calculated ram bump results
    if (moveResponse && moveResponse.isRamBump && moveResponse.bumpedPieceToRow !== null && moveResponse.bumpedPieceToCol !== null) {
      // Store the piece that was at the destination (to be bumped)
      const bumpedPiece = newBoard[toRow][toCol];
      
      // Move the ram to the destination
      newBoard[toRow][toCol] = movedPiece;
      newBoard[fromRow][fromCol] = null;
      
      // Place the bumped piece at its new location
      if (bumpedPiece) {
        newBoard[moveResponse.bumpedPieceToRow][moveResponse.bumpedPieceToCol] = bumpedPiece;
      }
    } else {
      // Normal move (non-ram or ram to empty square)
      newBoard[toRow][toCol] = movedPiece;
      newBoard[fromRow][fromCol] = null;
    }
    
    // Update the board state
    setBoard(newBoard);

    // Online-multiplayer: notify the opponent of this move via socket. Skipped
    // when applying a move we RECEIVED (isRemoteMove=true) to avoid a loop, and
    // skipped for AI moves (no remote peer).
    if (mode === 'online' && !isRemoteMove && !isAiMove && onMove) {
      onMove({ fromRow, fromCol, toRow, toCol, promotionPiece: promotionPiece || null });
    }

    // Clear selection and any invalid square highlighting
    setSelectedSquare(null);
    setInvalidSquare(null);
    
    // **ADDED BACK**: Show AI move highlight if this was an AI move
    if (isAiMove) {
      setLastAiMove({ row: toRow, col: toCol });
      setTimeout(() => {
        setLastAiMove(null);
      }, 1000);
    } else {
      // Track human moves for pondering hit detection
      setLastHumanMove({ fromRow, fromCol, toRow, toCol });
    }
    
    // Force re-render with multiple state updates
    const newRenderKey = Date.now();
    setBoardRenderKey(newRenderKey);
    setForceRender(prev => prev + 1);
    
    log('🔄 Board render key set to:', newRenderKey);
    log('🔄 Force render triggered');

    // Create move record for history - **FIXED**: Use server response for ram captures
    let actualCapturedType = null;
    if (piece.type === 'ram') {
      // For rams, use server response to determine if there was a capture
      if (moveResponse && moveResponse.capturedPiece) {
        actualCapturedType = moveResponse.capturedPiece;
        log('🐏 CLIENT: Server confirmed ram capture:', moveResponse.capturedPiece);
      } else {
        actualCapturedType = null;
        log('🐏 CLIENT: Server confirmed ram bump (no capture)');
      }
    } else if (capturedPiece) {
      // For other pieces, capture is straightforward
      actualCapturedType = capturedPiece.type;
    }

    const moveRecord = {
      from: { row: fromRow, col: fromCol },
      to: { row: toRow, col: toCol },
      piece: piece.type,
      color: piece.color,
      captured: actualCapturedType,
      promotion: promotionPiece || null, // Include promotion information
      timestamp: new Date().toISOString()
    };
    
    log('📝 Recording move:', moveRecord);
    
    // Update game state with the move - **FIXED**: Use server response for ram captures
    setGameState(prevState => {
      let updatedCapturedPieces = prevState.capturedPieces;
      
      // For ram moves, use server response to determine captures
      if (piece.type === 'ram' && moveResponse && moveResponse.capturedPiece && capturedPiece) {
        // Server confirmed a capture occurred (piece was bumped off board)
        updatedCapturedPieces = {
          ...prevState.capturedPieces,
          [capturedPiece.color]: [...(prevState.capturedPieces[capturedPiece.color] || []), capturedPiece]
        };
        log('🐏 CLIENT: Added ram-captured piece to list:', capturedPiece);
      } else if (piece.type === 'ram' && capturedPiece) {
        log('🐏 CLIENT: Ram bump - piece was moved, not captured');
      } else if (actualCapturedType && capturedPiece) {
        // Non-ram pieces: if there was a piece at destination, it was captured  
        updatedCapturedPieces = {
          ...prevState.capturedPieces,
          [capturedPiece.color]: [...(prevState.capturedPieces[capturedPiece.color] || []), capturedPiece]
        };
        log('📦 CLIENT: Added captured piece to list:', capturedPiece);
      }

      const newState = {
        ...prevState,
        moves: [...(prevState.moves || []), moveRecord],
        moveHistory: [...(prevState.moveHistory || []), moveRecord],
        capturedPieces: updatedCapturedPieces
      };
      
      log('🎮 Updated game state:', newState);
      return newState;
    });

    // CALCULATE ACTUAL ELAPSED TIME FOR THIS TURN
    const turnStartTime = turnStartTimeRef.current[currentPlayer];
    const currentTurnTime = turnStartTime ? Math.floor((Date.now() - turnStartTime) / 1000) : 0;
    const currentTotalTime = timeLeft[currentPlayer] || 0;
    
    // Switch player turns
    const nextPlayer = currentPlayer === 'white' ? 'black' : 'white';
    log(`🔄 Turn switch: ${currentPlayer} → ${nextPlayer}`);
    log(`⏱️ ${currentPlayer} turn time: ${currentTurnTime}s (from timestamp ${turnStartTime})`);
    log(`⏱️ ${currentPlayer} total before: ${currentTotalTime}s`);
    
    // Add current player's turn time to their total time
    setTimeLeft(prev => {
      const newTotal = (prev[currentPlayer] || 0) + currentTurnTime;
      log(`⏱️ ${currentPlayer} total after: ${newTotal}s`);
      return {
        ...prev,
        [currentPlayer]: newTotal
      };
    });
    
    // Reset display timer for next turn (will be updated by the interval)
    setTurnTimeLeft(prev => ({ ...prev, [nextPlayer]: 0 }));
    
    setCurrentPlayer(nextPlayer);
    
    log('🔄 Switching from', currentPlayer, 'to', nextPlayer);

    // Check for endgame conditions
    log('🏁 CHECKMATE DETECTION: Starting legal move search for', nextPlayer);
    
    // **CRITICAL FIX**: Temporarily disable bypass mode for accurate checkmate detection
    const originalBypassMode = gameLogic.bypassValidation;
    gameLogic.bypassValidation = false;
    log('🔒 CHECKMATE: Disabled bypass mode for accurate piece validation');
    
    const legalMoves = [];
    let moveCount = 0;
    
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        const piece = newBoard[row][col];
        if (piece && piece.color === nextPlayer) {
          for (let r = 0; r < 9; r++) {
            for (let c = 0; c < 9; c++) {
              moveCount++;
              if (gameLogic.isValidMove(newBoard, row, col, r, c, nextPlayer)) {
                legalMoves.push({ from: [row, col], to: [r, c] });
                
                // **DEBUG**: Log potential moves for debugging
                if (piece.type === 'donkey') {
                  log(`✅ DONKEY LEGAL MOVE: ${piece.type} (${row},${col}) → (${r},${c})`);
                }
              }
            }
          }
        }
      }
    }
    
    // **CRITICAL**: Restore original bypass mode
    gameLogic.bypassValidation = originalBypassMode;
    log(`🔍 CHECKMATE DETECTION: Checked ${moveCount} moves, found ${legalMoves.length} legal moves`);
    
    if (legalMoves.length > 0) {
      log(`🎯 LEGAL MOVES FOUND (first 3):`, legalMoves.slice(0, 3).map(move => 
        `${newBoard[move.from[0]][move.from[1]]?.type} (${move.from[0]},${move.from[1]}) → (${move.to[0]},${move.to[1]})`
      ));
    }
    
    if (legalMoves.length === 0) {
      const kingInCheck = gameLogic.isKingInCheck(newBoard, nextPlayer);
      const winner = kingInCheck ? currentPlayer : 'draw';
      const reason = kingInCheck
        ? `Checkmate! ${currentPlayer.charAt(0).toUpperCase() + currentPlayer.slice(1)} wins.`
        : 'Stalemate! The game is a draw.';
      
      setGameState(prevState => ({
        ...prevState,
        gameOver: true,
        winner: winner,
        gameOverReason: reason,
        isCheck: false
      }));
    } else {
      // Check if next player is AI and trigger their move
      if (isAIPlayer(nextPlayer)) {
        log('🎯 Next player is AI, setting up AI move trigger');
        setNextMovePlayer(nextPlayer);
      }
    }
    
    log('✅ Move completed successfully');
    return true;
  }, [board, gameState, currentPlayer, gameData, gameLogic, isAIPlayer, frozenKingZones, setPromotionDialog, timeLeft, turnTimeLeft, mode, onMove]);

  const isValidMove = useCallback((fromRow, fromCol, toRow, toCol) => {
    log('🔍 isValidMove called:', { fromRow, fromCol, toRow, toCol });
    
    if (!board || gameState?.gameOver) {
      log('❌ isValidMove failed: no board or game over');
      return false;
    }
    
    const piece = board[fromRow]?.[fromCol];
    
    if (!piece) {
      log('❌ isValidMove failed: no piece at source');
      return false;
    }
    
    if (piece.color !== currentPlayer) {
      log('❌ isValidMove failed: piece color mismatch', { pieceColor: piece.color, currentPlayer });
      return false;
    }
    
    log('✅ isValidMove: basic checks passed, calling gameLogic (server will validate)');
    const result = gameLogic.isValidMove(board, fromRow, fromCol, toRow, toCol, currentPlayer);
    
    log('🔍 CLIENT isValidMove result (server will validate):', result);
    return result;
  }, [board, gameState, gameLogic, currentPlayer]);

  const handleSquareClick = useCallback((row, col) => {
    // Only log in development for performance
    if (process.env.NODE_ENV === 'development') {
      log('🖱️ Square clicked:', { row, col, currentPlayer });
    }
    
    if (aiThinking || gameState?.gameOver) {
      return;
    }

    // Simplified AI check - only block if AI is actively thinking
    if (isAIPlayer(currentPlayer) && aiThinking) {
      return;
    }

    // Online-multiplayer: block clicks when it's the opponent's turn. The
    // opponent's move arrives via the opponentMove prop (useEffect below).
    if (mode === 'online' && gameData?.playerColor && currentPlayer !== gameData.playerColor) {
      return;
    }

    if (selectedSquare) {
      const moveIsValid = isValidMove(selectedSquare.row, selectedSquare.col, row, col);
      
      if (moveIsValid) {
        makeMove(selectedSquare.row, selectedSquare.col, row, col, false);
        setSelectedSquare(null);
      } else {
        // Check if clicking on same piece - keep selected
        const clickedPiece = board?.[row]?.[col];
        if (clickedPiece && clickedPiece.color === currentPlayer && 
            selectedSquare.row === row && selectedSquare.col === col) {
          // Clicking on same piece - keep it selected
          return;
        }
        
        // Check if clicking on another piece of same color - select it
        if (clickedPiece && clickedPiece.color === currentPlayer) {
          setSelectedSquare({ row, col });
          setInvalidSquare(null); // Clear any previous invalid move highlighting
        } else {
          // Clicking on invalid target - clear selection
          setSelectedSquare(null);
        }
      }
    } else {
      const piece = board?.[row]?.[col];
      
      // Add extensive debugging for black pieces (only in dev)
      if (process.env.NODE_ENV === 'development' && piece && piece.color === 'black') {
        log('🖤 BLACK PIECE CLICKED - EXTENSIVE DEBUG:', {
          pieceDetails: piece,
          currentPlayer: currentPlayer,
          currentPlayerType: typeof currentPlayer,
          pieceColor: piece.color,
          pieceColorType: typeof piece.color,
          exactMatch: piece.color === currentPlayer,
          stringComparison: String(piece.color) === String(currentPlayer),
          isAI: isAIPlayer(currentPlayer),
          aiThinking: aiThinking,
          gameOver: gameState?.gameOver,
          mode: mode,
          gameDataExists: !!gameData,
          boardExists: !!board,
          selectedSquare: selectedSquare,
          timestamp: new Date().toISOString()
        });
      }
      
      // ULTRA SIMPLIFIED LOGIC: Just check if piece belongs to current player
      if (piece && piece.color === currentPlayer) {
        log('✅ PIECE SELECTION ALLOWED - setting selectedSquare:', { row, col });
        setSelectedSquare({ row, col });
        setInvalidSquare(null); // Clear any previous invalid move highlighting
        log('✅ selectedSquare should now be set to:', { row, col });
      } else {
        log('❌ PIECE SELECTION DENIED:', {
          hasPiece: !!piece,
          pieceColor: piece?.color,
          currentPlayer,
          colorMatch: piece?.color === currentPlayer
        });
      }
    }
  }, [selectedSquare, board, currentPlayer, isValidMove, makeMove, aiThinking, gameState, isAIPlayer, setSelectedSquare, mode, gameData]);
  
  const makeAiMove = useCallback(async (currentBoard, player) => {
    // **FIX RACE CONDITION**: Check if AI is already making a move
    if (aiMoveLockRef.current) {
      log('🚫 AI move already in progress, skipping duplicate request');
      return;
    }
    
    if (gameState?.gameOver || !currentBoard || !gameData?.id) {
      log('🚫 AI move blocked - game over, no board, or no game ID');
      return;
    }
    
    // **LOCK AI MOVES**: Prevent simultaneous AI moves
    aiMoveLockRef.current = true;
    log('🔒 AI move lock acquired for player:', player);
    
    setAiThinking(true);
    setAiError(null);
    try {
      log('🚨 CRITICAL DEBUG: Requesting AI move for player:', player);
      log('🔍 Current game state:', {
        currentPlayer,
        isAI: isAIPlayer(player),
        expectedAI: 'black', // AI should be black in Human vs Computer
        gameMode: gameData?.mode
      });

      // Derive a per-move timeout from the AI difficulty. For `time-N` settings we know
      // the budget; for everything else (depth-N, named, neural) we fall back to 5 min so
      // deep searches aren't cut off prematurely.
      const FIVE_MINUTES_MS = 5 * 60 * 1000;
      const difficulty = gameData?.aiConfig?.difficulty;
      let timeoutMs = FIVE_MINUTES_MS;
      if (typeof difficulty === 'string' && difficulty.startsWith('time-')) {
        const seconds = parseInt(difficulty.slice('time-'.length), 10);
        if (Number.isFinite(seconds) && seconds > 0) {
          timeoutMs = seconds * 1000 + 30000; // budget + 30s buffer
        }
      }

      // Auto-retry transport errors (network only, not timeout — a search that genuinely
      // exceeded its budget will exceed it again on retry, and a 429 from the server's
      // duplicate-request guard isn't worth a tight retry loop).
      let move;
      const MAX_TRANSPORT_RETRIES = 1;
      let attempt = 0;
      while (true) {
        try {
          move = await api.requestAIMove(gameData.id, currentBoard, player, lastHumanMove, { timeoutMs });
          break;
        } catch (e) {
          const isVisible = typeof document !== 'undefined' && document.visibilityState === 'visible';
          const retriable = e.kind === 'network';
          if (retriable && attempt < MAX_TRANSPORT_RETRIES && isVisible) {
            attempt++;
            log(`🔁 AI request transport error (${e.kind}), retry ${attempt}/${MAX_TRANSPORT_RETRIES}: ${e.message}`);
            await new Promise(r => setTimeout(r, 500));
            continue;
          }
          throw e;
        }
      }
      if (move) {
        log('✅ AI move received:', move);
        
        // **UPDATE CHECK STATUS FROM AI MOVE RESPONSE**
        if (move.checkStatus) {
          log('👑 CLIENT: Updating check status from AI move response:', move.checkStatus);
          setServerCheckStatus(move.checkStatus);
          setHasServerCheckStatus(true);
        }
        
        log(`⏱️ AI (${player}) about to make move. Current turn times:`, turnTimeLeft);
        log(`⏱️ AI (${player}) about to make move. Current total times:`, timeLeft);
        log(`⏱️ AI (${player}) about to make move. Current player is:`, currentPlayer);
        
        // Server-side pickBestPromotion (2026-08-05) may have selected a
        // promotion piece already. Pass it through so makeMove skips the
        // random-choice fallback for AI moves that are pawn-to-last-rank.
        await makeMove(move.from.row, move.from.col, move.to.row, move.to.col, true, move.promotionPiece || null);
      } else {
        log('❌ No AI move returned');
      }
    } catch (error) {
      console.error('❌ Error making AI move:', error);

      // Server may have returned a structured game-over response (checkmate/stalemate detected
      // server-side). Honor it — that's a legitimate end-of-game, not a failure.
      const serverBody = error.body || (error.response && error.response.data);
      if (serverBody && serverBody.gameOver) {
        log('🏁 CLIENT: AI request returned game over state:', serverBody);
        const reason = serverBody.winner ?
          `Checkmate! ${serverBody.winner.charAt(0).toUpperCase() + serverBody.winner.slice(1)} wins.` :
          'Stalemate! The game is a draw.';

        setGameState(prevState => ({
          ...prevState,
          gameOver: true,
          winner: serverBody.winner,
          gameOverReason: reason
        }));
      } else {
        // Real failure (timeout / network / engine crash). Surface a retry option so the user
        // isn't stranded waiting for a move that will never come.
        setAiError({
          kind: error.kind || 'unknown',
          message: error.message || 'AI move failed',
          board: currentBoard,
          player,
        });
      }
    } finally {
      // **UNLOCK**: Always release the lock
      aiMoveLockRef.current = false;
      setAiThinking(false);
      log('🔓 AI move lock released');
    }
  }, [gameState, makeMove, gameData, setAiThinking, api]);

  // Assign the function to the ref after it's defined
  useEffect(() => {
    makeAiMoveRef.current = makeAiMove;
  }, [makeAiMove]);

  // When the tab returns from background, re-fire the AI move if it's still the AI's turn
  // and no request is in flight. Mobile Safari kills in-flight fetches when the tab is
  // hidden; without this, the user would land on a board that looks frozen.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handler = () => {
      if (document.visibilityState !== 'visible') return;
      if (aiMoveLockRef.current) return;          // request already in flight
      if (gameState?.gameOver) return;
      if (!board || !Array.isArray(board) || board.length !== 9) return;
      if (!isAIPlayer(currentPlayer)) return;
      log('🔁 Tab visible — AI turn pending, re-firing AI move');
      makeAiMove(board, currentPlayer);
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [board, currentPlayer, gameState, isAIPlayer, makeAiMove]);

  // Effect to run AI move when it's AI's turn
  useEffect(() => {
    if (!nextMovePlayer) return;
    
    // **FIX RACE CONDITION**: Don't trigger if AI is already thinking
    if (aiMoveLockRef.current) {
      log('🚫 AI move trigger blocked - AI already thinking');
      setNextMovePlayer(null); // Clear trigger to avoid loops
      return;
    }
    
    if (isAIPlayer(nextMovePlayer) && !promotionDialog && !gameState?.gameOver && board && Array.isArray(board) && board.length === 9) {
      const newBoard = board;
      const player = nextMovePlayer;
      
      // Clear the trigger immediately to prevent re-runs
      setNextMovePlayer(null);
      
      log('🎯 Triggering AI move for:', player);
      
      // Execute AI move
      if (makeAiMoveRef.current) {
        try {
          makeAiMoveRef.current(newBoard, player);
        } catch (error) {
          console.error('Error calling makeAiMove:', error);
        }
      } else if (makeAiMove) {
        makeAiMove(newBoard, player);
      }
    }
  }, [nextMovePlayer, isAIPlayer, promotionDialog, gameState, board, makeAiMove, setNextMovePlayer, makeAiMoveRef]);

  // Online-multiplayer: notify parent (via onGameOver) whenever this client
  // detects a terminal position — either by making the mating move locally
  // (line ~543 detection) or by applying an opponent's mating move. Both
  // clients will fire; server-side dedup in gameSocket.js prevents double
  // rating updates. We use a ref to fire only once per game.
  const gameOverEmittedRef = useRef(false);
  useEffect(() => {
    if (mode !== 'online' || !onGameOver) return;
    if (!gameState?.gameOver || gameOverEmittedRef.current) return;
    gameOverEmittedRef.current = true;
    const winner = gameState.winner || 'draw';
    const reason = gameState.gameOverReason || 'Game over';
    log('📤 Emitting online game_over:', { winner, reason });
    onGameOver(winner, reason);
  }, [gameState?.gameOver, gameState?.winner, gameState?.gameOverReason, mode, onGameOver]);

  // Online-multiplayer: apply an opponent's move received via the parent's
  // socket handler. `opponentMove` is a fresh object each socket event (parent
  // uses setState with a new reference), so the effect re-fires only on new
  // moves — no need to track "last applied move id". `isRemoteMove=true` on
  // makeMove skips the REST validation and the onMove re-emit.
  useEffect(() => {
    if (mode !== 'online' || !opponentMove) return;
    if (!board || !gameState || gameState.gameOver) return;
    const { fromRow, fromCol, toRow, toCol, promotionPiece } = opponentMove;
    if (fromRow == null || toRow == null) return;
    log('📥 Applying opponent move:', opponentMove);
    makeMove(fromRow, fromCol, toRow, toCol, false, promotionPiece || null, true);
    // Intentionally omit `board`/`gameState` from deps: those change every move
    // (including our own), which would cause re-application of stale opponentMove.
    // Only re-run when a fresh opponentMove object arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opponentMove, mode]);

  const createInitialBoard = useCallback(() => {
    const board = Array(9).fill(null).map(() => Array(9).fill(null));
    const setup = (row, color) => {
      board[row][0] = { type: 'donkey', color, hasMoved: false };
      board[row][1] = { type: 'knight', color, hasMoved: false };
      board[row][2] = { type: 'camel', color, hasMoved: false };
      board[row][3] = { type: 'advisor', color, hasMoved: false };
      board[row][4] = { type: 'king', color, hasMoved: false };
      board[row][5] = { type: 'advisor', color, hasMoved: false };
      board[row][6] = { type: 'camel', color, hasMoved: false };
      board[row][7] = { type: 'knight', color, hasMoved: false };
      board[row][8] = { type: 'donkey', color, hasMoved: false };
    };
    setup(0, 'white');
    setup(8, 'black');
    for (let col = 0; col < 9; col++) {
      board[2][col] = { type: 'pawn', color: 'white', hasMoved: false };
      board[6][col] = { type: 'pawn', color: 'black', hasMoved: false };
    }
    board[1][1] = { type: 'cannon', color: 'white', hasMoved: false };
    board[1][3] = { type: 'ram', color: 'white', hasMoved: false };
    board[1][4] = { type: 'spy', color: 'white', hasMoved: false };
    board[1][5] = { type: 'ram', color: 'white', hasMoved: false };
    board[1][7] = { type: 'cannon', color: 'white', hasMoved: false };
    board[7][1] = { type: 'cannon', color: 'black', hasMoved: false };
    board[7][3] = { type: 'ram', color: 'black', hasMoved: false };
    board[7][4] = { type: 'spy', color: 'black', hasMoved: false };
    board[7][5] = { type: 'ram', color: 'black', hasMoved: false };
    board[7][7] = { type: 'cannon', color: 'black', hasMoved: false };
    return board;
  }, []);

  const initializeGame = useCallback(() => {
    if (gameInitialized) {
      return;
    }
    
    console.log('🔄 Game initialization started - resetting AI trigger');
    // Reset AI trigger flag for new/resumed games
    aiTriggerRan.current = false;
    aiMoveLockRef.current = false;
    
    try {
      const importedGame = gameData?.importedGame;
      if (importedGame && importedGame.board) {
        console.log('📥 Loading imported/resumed game, current player:', importedGame.currentPlayer);
        setBoard(importedGame.board);
        setCurrentPlayer(importedGame.currentPlayer || 'white');
        setGameState({
          moves: importedGame.moves || [],
          moveHistory: importedGame.moveHistory || importedGame.moves || [],
          capturedPieces: importedGame.capturedPieces || { white: [], black: [] },
          gameOver: importedGame.gameOver || false,
          winner: importedGame.winner || null,
          gameOverReason: importedGame.gameOverReason || '',
          isCheck: importedGame.isCheck || false,
        });
      } else {
        setBoard(createInitialBoard());
        setCurrentPlayer('white');
        setGameState({
          moves: [],
          moveHistory: [],
          capturedPieces: { white: [], black: [] },
          gameOver: false,
          winner: null,
          gameOverReason: '',
          isCheck: false,
        });
      }
      
      setGameInitialized(true);
      
      // Initialize turn start time for the current player
      const currentPlayerForTimer = importedGame?.currentPlayer || 'white';
      turnStartTimeRef.current[currentPlayerForTimer] = Date.now();
      
      // Note: Frozen zones are now handled server-side and returned with move responses
    } catch (error) {
      console.error('❌ Error during game initialization:', error);
      // Still set as initialized to prevent infinite loop
      setGameInitialized(true);
    }
  }, [createInitialBoard, gameData]);

  // Initialize game when gameData is available
  useEffect(() => {
    if (!gameInitialized && gameData) {
      initializeGame();
    }
  }, [gameData, gameInitialized, initializeGame]);

  // Check if initial player is AI and trigger their move
  // OPTIMIZED: Only run AI trigger check once when game initializes (prevents CPU spike)
  const aiTriggerRan = useRef(false);
  const aiTriggerTimeoutRef = useRef(null);
  
  useEffect(() => {
    console.log('🔄 AI Trigger useEffect fired', { 
      aiTriggerRan: aiTriggerRan.current, 
      aiMoveLock: aiMoveLockRef.current,
      gameInitialized,
      currentPlayer 
    });
    
    // Don't check gameInitialized here - check it in the timeout when state is settled
    if (aiTriggerRan.current || aiMoveLockRef.current) {
      console.log('⏸️ AI trigger blocked by ref flags');
      return;
    }
    
    // Clear any existing timeout
    if (aiTriggerTimeoutRef.current) {
      clearTimeout(aiTriggerTimeoutRef.current);
    }
    
    // Add a delay to ensure state has settled after game initialization
    aiTriggerTimeoutRef.current = setTimeout(() => {
      console.log('⏰ Timeout fired, checking conditions...');
      
      // Now check gameInitialized INSIDE the timeout when React state has updated
      if (!gameInitialized) {
        console.log('❌ Game not initialized');
        return;
      }
      
      console.log('✅ Game is initialized, checking AI conditions...');
      
      // Check each condition individually
      const hasGameState = !!gameState;
      const notGameOver = !gameState?.gameOver;
      const hasCurrentPlayer = !!currentPlayer;
      const isAI = isAIPlayer(currentPlayer);
      const notAiThinking = !aiThinking;
      const noPromotion = !promotionDialog;
      const hasValidBoard = board && Array.isArray(board) && board.length === 9;
      
      console.log('🔍 AI Conditions:', { 
        hasGameState, 
        notGameOver, 
        hasCurrentPlayer, 
        currentPlayer,
        isAI, 
        notAiThinking, 
        noPromotion, 
        hasValidBoard 
      });
      
      if (hasGameState && notGameOver && hasCurrentPlayer && isAI && notAiThinking && noPromotion && hasValidBoard) {
        // Trigger for both new games and resumed games where it's AI's turn
        console.log('🎯 AI triggered for', currentPlayer);
        setNextMovePlayer(currentPlayer);
        aiTriggerRan.current = true;
      } else if (hasGameState && hasCurrentPlayer && isAI) {
        // Log why AI didn't trigger if it's supposed to be AI's turn
        console.log('⚠️ AI should move but blocked:', { notGameOver, notAiThinking, noPromotion, hasValidBoard });
      } else {
        console.log('❌ AI trigger failed - not AI player or missing required state');
      }
    }, 500);
    
    return () => {
      if (aiTriggerTimeoutRef.current) {
        clearTimeout(aiTriggerTimeoutRef.current);
      }
    };
  }, [gameInitialized, currentPlayer, gameState, aiThinking, promotionDialog, board, isAIPlayer]);

  // OPTIMIZED: Memoize check status - only calculate once per board state, not 81 times!
  const checkStatus = React.useMemo(() => {
    // Always default to no check initially
    const defaultStatus = { white: false, black: false };
    
    if (hasServerCheckStatus) {
      return serverCheckStatus || defaultStatus;
    }
    
    // Only calculate if we have a valid board
    if (!board || !Array.isArray(board) || board.length !== 9) {
      return defaultStatus;
    }
    
    try {
      return {
        white: gameLogic.isKingInCheck(board, 'white'),
        black: gameLogic.isKingInCheck(board, 'black')
      };
    } catch (err) {
      console.error('Error checking king status:', err);
      return defaultStatus;
    }
  }, [board, hasServerCheckStatus, serverCheckStatus, gameLogic]);
  
  const isPlayerInCheck = useCallback((player) => {
    return checkStatus[player] || false;
  }, [checkStatus]);
  
  const handlePromotion = useCallback(async (pieceType) => {
    if (!promotionDialog) return;
    const { row, col, color, fromRow, fromCol } = promotionDialog;
    
    log('👑 PROMOTION: Selected piece type:', pieceType, 'for', color, 'pawn');
    log('👑 PROMOTION: Making move from', fromRow, fromCol, 'to', row, col, 'with promotion to', pieceType);
    
    // Clear the promotion dialog first
    setPromotionDialog(null);
    
         // Make the move with the selected promotion piece
     await makeMove(fromRow, fromCol, row, col, false, pieceType);
  }, [promotionDialog, makeMove]);

  const renderSquare = (boardRow, boardCol, displayRow, displayCol) => {
    const piece = board?.[boardRow]?.[boardCol];
    const isSelected = selectedSquare && selectedSquare.row === boardRow && selectedSquare.col === boardCol;
    // Check if this square contains a king that is in check
    const isCheck = piece?.type === 'king' && checkStatus && checkStatus[piece.color] === true;
    const isInvalid = invalidSquare && invalidSquare.row === boardRow && invalidSquare.col === boardCol;
    const isLastAiMove = lastAiMove && lastAiMove.row === boardRow && lastAiMove.col === boardCol;
    
    // Debug check highlighting
    // King check highlighting handled by CSS
    
    // Check if this square is in a frozen king zone
    const isFrozenZone = Object.values(frozenKingZones).some(zone => 
      zone.frozenSquares && zone.frozenSquares.some(square => 
        square.row === boardRow && square.col === boardCol
      )
    );
    
    // Square rendering logic
    
    let squareClass = 'square';
    if ((displayRow + displayCol) % 2 === 0) squareClass += ' light'; else squareClass += ' dark';
    if (isSelected) squareClass += ' selected';
    
    // CSS test removed - CSS works fine!
    
    if (isCheck) {
      squareClass += ' check';
    }
    if (isInvalid) squareClass += ' invalid-move';
    if (isFrozenZone) squareClass += ' frozen-zone';
    if (isLastAiMove) squareClass += ' ai-move-flash'; // **ADDED BACK**: AI move square highlighting

    // Create a unique key that includes board render key and piece info to force re-render
    const pieceKey = piece ? `${piece.color}-${piece.type}-${piece.hasMoved}` : 'empty';
    const squareKey = `${boardRow}-${boardCol}-${pieceKey}-${boardRenderKey}-${forceRender}`;

    return (
      <div 
        key={squareKey} 
        className={squareClass} 
        onClick={() => handleSquareClick(boardRow, boardCol)}
      >
        {piece && (
          <div className={`piece ${piece.color} ${isLastAiMove ? 'ai-piece-flash' : ''}`}>
            <img 
              src={PIECE_IMAGES[piece.color][piece.type]} 
              alt={`${piece.color} ${piece.type}`}
              style={{ display: availableImages[`${piece.color}-${piece.type}`] ? 'block' : 'none' }}
            />
            {!availableImages[`${piece.color}-${piece.type}`] && PIECE_SYMBOLS[piece.color][piece.type]}
          </div>
        )}
      </div>
    );
  };

  const renderPromotionDialog = () => {
    if (!promotionDialog) return null;

    const { color } = promotionDialog;
    const promotionPieces = ['advisor', 'cannon', 'camel', 'knight', 'ram', 'donkey'];

    return (
      <div className="promotion-overlay">
        <div className="promotion-dialog">
          <h3>Promote Pawn</h3>
          <div className="promotion-options">
            {promotionPieces.map(pieceType => (
              <button
                key={pieceType}
                className="promotion-option"
                onClick={() => handlePromotion(pieceType)}
              >
                <img 
                  src={PIECE_IMAGES[color][pieceType]} 
                  alt={`${color} ${pieceType}`}
                  style={{ display: availableImages[`${color}-${pieceType}`] ? 'block' : 'none' }}
                />
                {!availableImages[`${color}-${pieceType}`] && PIECE_SYMBOLS[color][pieceType]}
                <span>{pieceType}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderBoard = () => {
    if (!board) {
      return <div>Loading...</div>;
    }
    
    const squares = [];
    // Reverse the row order so white pieces are at the bottom (row 0)
    for (let i = 8; i >= 0; i--) {
      for (let j = 0; j < 9; j++) {
        squares.push(renderSquare(i, j, i, j));
      }
    }
    
    // Board rendering complete
    return <div className="board" key={`board-${boardRenderKey}-${forceRender}`}>{squares}</div>;
  };

  // Test move function for debugging
  const testMove = () => {
    log('🧪 =============== TEST MOVE START ===============');
    log('🧪 Test move button clicked');
    
    // Test a simple pawn move from (2,4) to (3,4)
    const fromRow = 2, fromCol = 4, toRow = 3, toCol = 4;
    const piece = board?.[fromRow]?.[fromCol];
    
    log('🧪 Current game state:', {
      boardExists: !!board,
      boardLength: board?.length,
      currentPlayer,
      gameInitialized,
      boardRenderKey,
      forceRender
    });
    
    if (piece) {
      log('🧪 Found piece:', piece.type, piece.color);
      log('🧪 Current board state at (2,4):', board[2][4]);
      log('🧪 Current board state at (3,4):', board[3][4]);
      log('🧪 Current boardRenderKey before move:', boardRenderKey);
      log('🧪 Current forceRender before move:', forceRender);
      log('🧪 Attempting move from (2,4) to (3,4)');
      
      // Check if the move is valid first
      const isValid = isValidMove(fromRow, fromCol, toRow, toCol);
      log('🧪 Move validity check:', isValid);
      
      if (isValid) {
        log('🧪 Move is valid, executing...');
        makeMove(fromRow, fromCol, toRow, toCol, false);
        
        // Check state after React updates
        setTimeout(() => {
          log('🧪 === AFTER MOVE (timeout) ===');
          log('🧪 Board state at (2,4) after move:', board?.[2]?.[4]);
          log('🧪 Board state at (3,4) after move:', board?.[3]?.[4]);
          log('🧪 BoardRenderKey after move:', boardRenderKey);
          log('🧪 ForceRender after move:', forceRender);
          log('🧪 Current player after move:', currentPlayer);
          
          // Log the entire board state for
          log('🧪 Full board state after move:');
          board?.forEach((row, rowIndex) => {
            const rowPieces = row.map((cell, colIndex) => {
              if (cell) {
                return `${cell.color[0]}${cell.type[0]}`;
              }
              return '..';
            }).join(' ');
            log(`🧪 Row ${rowIndex}: ${rowPieces}`);
          });
          
          log('🧪 =============== TEST MOVE END ===============');
        }, 100);
      } else {
        log('🧪 Move is invalid, not executing');
        log('🧪 =============== TEST MOVE END ===============');
      }
    } else {
      log('🧪 No piece found at (2,4)');
      log('🧪 Full board state:');
      board?.forEach((row, rowIndex) => {
        const rowPieces = row.map((cell, colIndex) => {
          if (cell) {
            return `${cell.color[0]}${cell.type[0]}`;
          }
          return '..';
        }).join(' ');
        log(`🧪 Row ${rowIndex}: ${rowPieces}`);
      });
      log('🧪 =============== TEST MOVE END ===============');
    }
  };

  // Force refresh board state from server
  const forceRefreshBoard = async () => {
    log('🔄 =============== FORCE REFRESH BOARD START ===============');
    
    if (!gameData?.id) {
      console.error('❌ No game ID available for refresh');
      return;
    }
    
    try {
      log('🔄 Fetching latest game state from server...');
      const response = await api.getGame(gameData.id);
      
      if (!response || !response.board) {
        console.error('❌ Invalid response from server');
        return;
      }
      
      log('✅ Received game state from server:', {
        currentPlayer: response.currentPlayer,
        gameOver: response.gameOver,
        movesCount: response.moves?.length
      });
      
      // Update all state from server
      setBoard(response.board);
      setCurrentPlayer(response.currentPlayer || 'white');
      setFrozenKingZones(response.frozenKingZones || {});
      
      if (response.gameOver) {
        setGameState(prev => ({
          ...prev,
          gameOver: true,
          winner: response.winner,
          gameOverReason: response.gameOverReason || 'Game Over'
        }));
      } else {
        setGameState(prev => ({
          ...prev,
          gameOver: false,
          winner: null,
          gameOverReason: null,
          moves: response.moves || []
        }));
      }
      
      // Force re-render
      setBoardRenderKey(prev => prev + 1);
      setForceRender(prev => prev + 1);
      
      log('✅ Board state refreshed successfully');
      log('🔄 =============== FORCE REFRESH BOARD END ===============');
    } catch (error) {
      console.error('❌ Error refreshing board:', error);
    }
  };

  // Debug function to check current player state
  const debugCurrentPlayer = () => {
    log('🔍 =============== DEBUG CURRENT PLAYER ===============');
    log('🔍 Current player:', currentPlayer);
    log('🔍 Game mode:', mode);
    log('🔍 Game data:', gameData);
    log('🔍 White player name:', gameData?.whitePlayerName);
    log('🔍 Black player name:', gameData?.blackPlayerName);
    log('🔍 Is white AI?', isAIPlayer('white'));
    log('🔍 Is black AI?', isAIPlayer('black'));
    log('🔍 AI thinking:', aiThinking);
    log('🔍 Game over:', gameState?.gameOver);
    log('🔍 Game initialized:', gameInitialized);
    log('🔍 Board exists:', !!board);
    
    // Test clicking on a black piece
    const blackPiece = board?.[6]?.[4]; // Black pawn at (6,4)
    log('🔍 Black piece at (6,4):', blackPiece);
    
    if (blackPiece) {
      log('🔍 Can select black piece?:', {
        piece: blackPiece,
        pieceColor: blackPiece.color,
        currentPlayer: currentPlayer,
        colorMatch: blackPiece.color === currentPlayer,
        isAIPlayer: isAIPlayer(currentPlayer),
        canSelect: blackPiece.color === currentPlayer && !isAIPlayer(currentPlayer)
      });
    }
    
    log('🔍 =============== DEBUG END ===============');
  };

  // Add a useEffect to monitor board changes (disabled for performance)
  // useEffect(() => {
  //   if (board && board.length === 9) {
  //     log('🔄 Board state changed, new reference:', board);
  //     log('🔄 Board at (2,4):', board[2][4]);
  //     log('🔄 Board at (3,4):', board[3][4]);
  //     log('🔄 Board render key:', boardRenderKey);
  //     log('🔄 Force render counter:', forceRender);
  //   }
  // }, [board]);

  // Add debug effect for force render changes (disabled for performance)
  // useEffect(() => {
  //   if (forceRender > 0) {
  //     log('🔄 ForceRender changed to:', forceRender);
  //   }
  // }, [forceRender]);

  // Add a comprehensive debug button
  const debugPlayerState = () => {
    log('🔍 =============== COMPREHENSIVE DEBUG ===============');
    log('🔍 Current player:', currentPlayer);
    log('🔍 Game mode:', mode);
    log('🔍 Game data:', gameData);
    log('🔍 White player name:', gameData?.whitePlayerName);
    log('🔍 Black player name:', gameData?.blackPlayerName);
    log('🔍 Is white AI?', isAIPlayer('white'));
    log('🔍 Is black AI?', isAIPlayer('black'));
    log('🔍 AI thinking:', aiThinking);
    log('🔍 Game over:', gameState?.gameOver);
    log('🔍 Game initialized:', gameInitialized);
    log('🔍 Board exists:', !!board);
    log('🔍 Selected square:', selectedSquare);
    log('🔍 =============== DEBUG END ===============');
  };

  // Add debug function to test black piece selection
  const testBlackPieceSelection = () => {
    log('🧪 =============== TESTING BLACK PIECE SELECTION ===============');
    log('🧪 Current player:', currentPlayer);
    log('🧪 Board exists:', !!board);
    
    if (board) {
      // Find a black piece
      for (let row = 0; row < 9; row++) {
        for (let col = 0; col < 9; col++) {
          const piece = board[row][col];
          if (piece && piece.color === 'black') {
            log('🧪 Found black piece at:', { row, col, piece });
            log('🧪 Simulating click on black piece...');
            
            // Simulate the piece selection logic
            const canSelect = piece.color === currentPlayer;
            log('🧪 Can select?', canSelect);
            log('🧪 Current player matches piece color?', piece.color === currentPlayer);
            
            if (canSelect) {
              log('🧪 ✅ Would select piece:', { row, col });
              setSelectedSquare({ row, col });
              log('🧪 ✅ selectedSquare set to:', { row, col });
            } else {
              log('🧪 ❌ Would NOT select piece');
            }
            
            log('🧪 =============== TEST END ===============');
            return;
          }
        }
      }
      log('🧪 ❌ No black pieces found on board');
    } else {
      log('🧪 ❌ Board does not exist');
    }
    log('🧪 =============== TEST END ===============');
  };

  // Resign handler
  const handleResign = async () => {
    if (!gameData?.id) return;
    
    const confirmResign = window.confirm(
      `Are you sure you want to resign? ${currentPlayer === 'white' ? 'Black' : 'White'} will win.`
    );
    
    if (!confirmResign) return;
    
    try {
      const winner = currentPlayer === 'white' ? 'black' : 'white';
      await api.updateGameStatus(gameData.id, 'completed', winner);
      
      // Update local game state
      setGameState(prev => ({
        ...prev,
        gameOver: true,
        winner: winner
      }));
      
      alert(`Game over! ${winner.charAt(0).toUpperCase() + winner.slice(1)} wins by resignation.`);
    } catch (error) {
      console.error('Failed to resign:', error);
      alert('Failed to resign. Please try again.');
    }
  };

  if (!board || !gameState) {
    return <div>Initializing game...</div>;
  }
  
  // Final render logging removed
  
  return (
    <div className="game-container">
      {/* Mobile-only top-right kebab. Game actions from the deleted
          "Game Actions & Info" collapse now live here. */}
      <KebabMenu
        canSave={!!(onSaveAndExit && gameData?.id && !gameState?.gameOver)}
        canResign={!!(gameState && !gameState.gameOver)}
        onOpenMoveHistory={() => setShowMoveHistory(true)}
        onExportCsv={() => exportGameHistoryCsv({
          moves: (gameState?.moveHistory || []).map((m, i) => ({
            ...m,
            player_color: m.player || (i % 2 === 0 ? 'white' : 'black'),
            from_row: m.from?.row, from_col: m.from?.col,
            to_row:   m.to?.row,   to_col:   m.to?.col,
            piece_type: m.piece,
            captured_piece: m.captured,
          })),
          gameData,
        })}
        onExportPosition={() => exportBoardPosition(board)}
        onSaveAndExit={onSaveAndExit}
        onResign={handleResign}
      />
      <MoveHistoryOverlay
        open={showMoveHistory}
        onClose={() => setShowMoveHistory(false)}
        gameState={gameState}
      />
      <div className="game-board-layout">
        <div className="left-panel">
          <GameHistory 
            gameState={gameState}
            gameData={gameData}
            board={board}
            onBackToMenu={onBackToMenu}
            onBackToSetup={onBackToSetup}
            onResign={handleResign}
            onSaveAndExit={onSaveAndExit}
          />
          {process.env.NODE_ENV === 'development' && (
            <div className="test-move-section">
              <button 
              onClick={testMove} 
              className="test-move-button"
              style={{
                backgroundColor: '#ff0000',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                margin: '10px',
                cursor: 'pointer',
                borderRadius: '5px'
              }}
            >
              Test Move (e3→e4)
            </button>
            <button 
              onClick={debugCurrentPlayer} 
              className="debug-button"
              style={{
                backgroundColor: '#0066cc',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                margin: '10px',
                cursor: 'pointer',
                borderRadius: '5px'
              }}
            >
              Debug Current Player
            </button>

            <button 
              onClick={debugPlayerState}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                margin: '10px',
                cursor: 'pointer',
                borderRadius: '5px'
              }}
            >
              Comprehensive Debug
            </button>
            
            <button 
              onClick={testBlackPieceSelection}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                margin: '10px',
                cursor: 'pointer',
                borderRadius: '5px'
              }}
            >
              Test Black Piece Selection
            </button>

            <button 
              onClick={forceRefreshBoard}
              style={{
                backgroundColor: '#ffc107',
                color: 'black',
                border: 'none',
                padding: '10px 20px',
                margin: '10px',
                cursor: 'pointer',
                borderRadius: '5px',
                fontWeight: 'bold'
              }}
              title="Sync board state with server - use if game appears stuck"
            >
              🔄 Force Refresh Board
            </button>

            <button 
              onClick={() => {
                log('🔍 =============== TEST BLACK PIECE CLICK ===============');
                log('🔍 Attempting to simulate click on black piece at (6,4)');
                
                // Test the click logic directly
                const row = 6, col = 4;
                const piece = board?.[row]?.[col];
                
                log('🔍 Click simulation details:', {
                  row, col,
                  piece: piece ? `${piece.color} ${piece.type}` : 'empty',
                  currentPlayer,
                  aiThinking,
                  gameOver: gameState?.gameOver,
                  isAIPlayer: isAIPlayer(currentPlayer),
                  mode,
                  gameData: {
                    whitePlayerName: gameData?.whitePlayerName,
                    blackPlayerName: gameData?.blackPlayerName
                  }
                });
                
                // Actually simulate the click
                if (piece) {
                  log('🔍 Simulating handleSquareClick(6, 4)');
                  handleSquareClick(6, 4);
                } else {
                  log('❌ No piece at (6,4) to click');
                }
                
                log('🔍 =============== END TEST ===============');
              }}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                margin: '10px',
                cursor: 'pointer',
                borderRadius: '5px'
              }}
            >
              Test Black Piece Click
            </button>
            </div>
          )}
        </div>
        {aiError && (
          <div
            role="alert"
            style={{
              // Fixed overlay at viewport top — doesn't displace the board.
              position: 'fixed',
              top: '16px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              maxWidth: '480px',
              width: 'calc(100% - 32px)',
              background: '#fff3cd',
              border: '1px solid #ffeeba',
              color: '#856404',
              borderRadius: '8px',
              padding: '12px 16px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              fontSize: '0.95rem',
            }}
          >
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <strong>AI couldn't respond.</strong>{' '}
              <span style={{ opacity: 0.85 }}>{aiError.message}</span>
            </div>
            <button
              onClick={() => {
                const { board: savedBoard, player: savedPlayer } = aiError;
                setAiError(null);
                if (savedBoard && savedPlayer && !aiMoveLockRef.current) {
                  makeAiMove(savedBoard, savedPlayer);
                }
              }}
              style={{
                background: '#007bff',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
              }}
            >
              Try again
            </button>
          </div>
        )}
        <div className="board-container" key={`board-container-${boardRenderKey}-${forceRender}-${currentPlayer}`}>
          {renderBoard()}
          {renderPromotionDialog()}

          {/* Mobile-only top strip (renders above the board via
              flex-direction: column-reverse on .board-container at ≤600px).
              Replaces the old collapsible "Game Actions & Info" panel —
              actions moved into the top-right kebab menu. */}
          <MobileGameHeader
            gameState={gameState}
            timeLeft={timeLeft}
            turnTimeLeft={turnTimeLeft}
          />
        </div>
        <div className="desktop-game-info">
          <GameInfo
            currentPlayer={currentPlayer}
            gameState={gameState}
            onBackToMenu={onBackToMenu}
            onBackToSetup={onBackToSetup}
            mode={mode}
            whitePlayerName={gameData.whitePlayerName}
            blackPlayerName={gameData.blackPlayerName}
            timeControls={gameData.timeControls}
            timeLeft={timeLeft}
            turnTimeLeft={turnTimeLeft}
          />
        </div>
      </div>
    </div>
  );
};

export default GameBoard; 